import _ from 'lodash'

import {
    getParameterByName,
    EnvironmentDetail,
    LatencyMetric,
    CountMetric,
} from '@amzn/ihm-associate-tools-js-lib'

import { getIIBRefreshToken } from '@atredux/selectors/ihmLoginTokenInfoSelector'
import { store } from '@atredux/store'
import { IhmIdentityBrokerTokenProvider } from '@api/utils/IhmIdentityBrokerTokenProvider'

const CLIENT_ID = {
    BETA: '3gpgb9cjd2mftr9if53cbitrvj',
    GAMMA: '3rlfii3q65bci2djku5k6qiogb',
    PROD:  '4no4s3po212g591ubhko1svv15',
}

export class IhmIdentityBrokerServiceClient {
    public static getIhmIdentityBrokerLoginUri (): string {
        console.log('ATBase: Fetching IIB webapp URI...')

        const amazonDomain = EnvironmentDetail.getDomain()

        let uri: string = ''
        switch (amazonDomain) {
        case 'prod':
            uri += 'https://prod.us-west-2.identitybroker.ihm.amazon.dev'
            break
        case 'gamma':
            uri += 'https://gamma.us-east-1.identitybroker.ihm.amazon.dev'
            break
        case 'test':
            uri += 'https://beta.identitybroker.ihm.amazon.dev'
            break
        default:
            uri += 'https://beta.identitybroker.ihm.amazon.dev'
            break
        }

        uri += '?client_id=' + IhmIdentityBrokerServiceClient.getClientId()
        uri += '&redirect_uri=' + window.location.origin + window.location.pathname
        uri += '?' + 'auth%3Dihm-identity-broker' + '%26domain%3D' + amazonDomain
        console.log('ATBase: Returning IIB login URI: ', uri)
        return uri
    }

    public static getIhmIdentityBrokerVip (): string {
        console.info('ATBase: Fetching environment data for IhmIdentityBroker...')

        const region = (EnvironmentDetail.getRegion() || '').toLowerCase()
        const amazonDomain = EnvironmentDetail.getDomain()

        console.info('ATBase: Got environment data for IhmIdentityBroker: ', region, amazonDomain)

        // TODO: Configure all VIPs/origins for IIB endpoints
        switch (amazonDomain) {
        case 'prod':
            return 'https://prod-iib.auth.us-west-2.amazoncognito.com'
        case 'gamma':
            return 'https://gamma-iib.auth.us-east-1.amazoncognito.com'
        case 'test':
            return 'https://beta-iib.auth.us-west-2.amazoncognito.com'
        default:
            return 'https://beta-iib.auth.us-west-2.amazoncognito.com'
        }
    }

    public static getClientId (): string {
        const amazonDomain = EnvironmentDetail.getDomain()

        // TODO: Configure all client IDs for IIB endpoints
        switch (amazonDomain) {
        case 'prod':
            return CLIENT_ID.PROD
        case 'gamma':
            return CLIENT_ID.GAMMA
        case 'test':
            return CLIENT_ID.BETA
        default:
            return CLIENT_ID.BETA
        }
    }

    public static getAssociateToolsBaseUrl (): string {
        const absUrl = window.location.href

        return absUrl.split('?')[0]
    }

    public static getLocale (): string {
        return getParameterByName('locale')
    }

    public static getUriParamsWithEnvironmentDetails (): string {
        const amazonDomain = EnvironmentDetail.getDomain()
        const initialTool = EnvironmentDetail.getInitialTool()
        const initialZoneInfo = EnvironmentDetail.getInitialZoneInfo()
        const suppressConnections = EnvironmentDetail.getSuppressConnections()

        let result = 'auth%3Dihm-identity-broker'

        if (amazonDomain) {
            result += '%26domain%3D' + amazonDomain
        }
        if (initialTool) {
            result += '%26initialTool%3D' + encodeURIComponent(encodeURIComponent(initialTool))
        }
        if (initialZoneInfo) {
            // encodeURIComponent twice because that value is used in return url that should be encoded
            if (initialZoneInfo.zoneName) {
                result += '%26initialZoneName%3D' + encodeURIComponent(encodeURIComponent(initialZoneInfo.zoneName))
            }
            if (initialZoneInfo.subzoneName) {
                result += '%26initialSubzoneName%3D' + encodeURIComponent(encodeURIComponent(initialZoneInfo.subzoneName))
            }
        }
        if (suppressConnections && suppressConnections === 'true') {
            result += '%26suppressConnections%3Dtrue'
        }

        return result
    }

    /**
     * Creates a URL that can be used to sign in using the auth code flow.
     */
    public static getIhmIdentityBrokerSignInUrlForAuthFlow (): string {
        let redirectUri
        const associateToolsBaseUrl = IhmIdentityBrokerServiceClient.getAssociateToolsBaseUrl()
        const ihmIdentityBrokerVip = IhmIdentityBrokerServiceClient.getIhmIdentityBrokerVip()

        const clientId = IhmIdentityBrokerServiceClient.getClientId()
        const code = getParameterByName('code')

        redirectUri = associateToolsBaseUrl + '?' + IhmIdentityBrokerServiceClient.getUriParamsWithEnvironmentDetails()
        console.log('ATBase: Redirect URI for IIB call: ', redirectUri)

        const endpointPath = '/oauth2/token'
        const url = ihmIdentityBrokerVip + endpointPath + '?client_id=' + clientId + '&redirect_uri=' + redirectUri
                        + '&grant_type=authorization_code' + '&code=' + code
        console.log('ATBase: IhmIdentityBroker URL: ', url)
        return url
    }

    /**
     * Creates a URL that can be used to sign in using the refresh code flow.
     */
    public static getIhmIdentityBrokerSignInUrlForRefreshFlow (): string {
        let redirectUri
        const associateToolsBaseUrl = IhmIdentityBrokerServiceClient.getAssociateToolsBaseUrl()
        const ihmIdentityBrokerVip = IhmIdentityBrokerServiceClient.getIhmIdentityBrokerVip()

        const clientId = IhmIdentityBrokerServiceClient.getClientId()
        const code = getParameterByName('code')

        redirectUri = associateToolsBaseUrl + '?' + IhmIdentityBrokerServiceClient.getUriParamsWithEnvironmentDetails()
        console.log('ATBase: Redirect URI for IIB call: ', redirectUri)

        const endpointPath = '/oauth2/token'
        const url = ihmIdentityBrokerVip + endpointPath + '?client_id=' + clientId + '&redirect_uri=' + redirectUri
                        + '&grant_type=refresh_token' + '&refresh_token=' + getIIBRefreshToken(store.getState())
        console.log('ATBase: IhmIdentityBroker URL: ', url)
        return url
    }

    /**
     * Calls IhmIdentityBroker's endpoint to retrieve the token using the refresh token (from previous step).
     * This requires relies on retrieving the redirect url and the state from getIhmIdentityBrokerSignInUrlForRefreshFlow function.
     */
    public static refreshTokens (): Promise<any> {
        const url = IhmIdentityBrokerServiceClient.getIhmIdentityBrokerSignInUrlForRefreshFlow()

        const headers: HeadersInit = {
            'Content-Type': 'application/x-www-form-urlencoded',
        }

        console.log('ATBase: Making request to get token info from URL: ', url)
        const retrieveAuthTokenLatency = new LatencyMetric(undefined, 'RetrieveAuthTokenLatency', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        const retrieveAuthTokenSuccess = new CountMetric(undefined, 'RetrieveAuthTokenSuccess', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        const retrieveAuthTokenError = new CountMetric(undefined, 'RetrieveAuthTokenFailure', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        retrieveAuthTokenLatency.start()
        return fetch(url, {
            method: 'POST',
            headers,
        }).then((response) => {
            console.log('ATBase: Successful call to IIB endpoint.')
            retrieveAuthTokenLatency.stop()
            retrieveAuthTokenSuccess.count(1)
            retrieveAuthTokenError.count(0)
            return response.json()
        }).then((iibData) => {
            console.log('ATBase: IhmIdentityBrokerRefreshFlow: Successful IhmIdentityBroker authentication.')
            IhmIdentityBrokerTokenProvider.setToken(IhmIdentityBrokerTokenProvider.getRefreshToken(), iibData.token_type, iibData.id_token)
        }).catch((exception) => {
            console.error('ATBase: Unsuccessful call to IIB endpoint.')
            retrieveAuthTokenLatency.stop()
            retrieveAuthTokenError.count(1)
            retrieveAuthTokenSuccess.count(0)
            return Promise.reject(exception)
        })
    }

    /**
     * Calls IhmIdentityBroker's endpoint to retrieve the token using the code (extracted from the URL).
     * This requires relies on retrieving the redirect url and the state from getIhmIdentityBrokerSignInUrlForAuthFlow function.
     *
     * @return {Promise} Returns a an object with the tokens with success.
     *  Successful response example:
     *      {
     *          access_token: {String}, // This is the token that is sent to the backend services.
     *          expires_in: {Number}, // Time in seconds until the token expires.
     *          id_token: {String}, // Id token for the logged in user.
     *          scope: {String}, // Space separated scopes.
     *          token_type: {String} // The token type (usually "Bearer").
     *      }
     */
    public static retrieveAuthToken (): Promise<any> {
        const url = IhmIdentityBrokerServiceClient.getIhmIdentityBrokerSignInUrlForAuthFlow()

        const headers: HeadersInit = {
            'Content-Type': 'application/x-www-form-urlencoded',
        }

        console.log('ATBase: Making request to get token info from URL: ', url)
        const retrieveAuthTokenLatency = new LatencyMetric(undefined, 'RetrieveAuthTokenLatency', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        const retrieveAuthTokenSuccess = new CountMetric(undefined, 'RetrieveAuthTokenSuccess', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        const retrieveAuthTokenError = new CountMetric(undefined, 'RetrieveAuthTokenFailure', 'IhmFlyingDutchman', 'IhmLoginServiceClient.retrieveAuthToken')
        retrieveAuthTokenLatency.start()
        return fetch(url, {
            method: 'POST',
            headers,
        }).then((response) => {
            console.log('ATBase: Successful call to IIB endpoint.')
            retrieveAuthTokenLatency.stop()
            retrieveAuthTokenSuccess.count(1)
            retrieveAuthTokenError.count(0)
            return response.json()
        }).catch((exception) => {
            console.error('ATBase: Unsuccessful call to IIB endpoint.')
            retrieveAuthTokenLatency.stop()
            retrieveAuthTokenError.count(1)
            retrieveAuthTokenSuccess.count(0)
            return Promise.reject(exception)
        })
    }
}
